/**
 * @file     InvestorSectionTracker.tsx
 * @purpose  Client edge of AsARentalSection: sends investor_section_view once
 *           when the section scrolls into view, and cta_click (listing_type
 *           sale) when a link inside it is followed.
 */
'use client';

import { useEffect, useRef, type MouseEvent, type ReactNode } from 'react';
import { useListingEvents } from '@/hooks/useListingEvents';
import { useAnalytics } from '@/hooks/useAnalytics';

interface Props {
  listingKey: string;
  rentSource: string;
  hasRemarksRule: boolean;
  children: ReactNode;
}

export function InvestorSectionTracker({ listingKey, rentSource, hasRemarksRule, children }: Props) {
  const track = useListingEvents();
  const { trackCtaClick } = useAnalytics();
  const ref = useRef<HTMLDivElement>(null);
  const sent = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => {
        if (sent.current || !entries.some((e) => e.isIntersecting)) return;
        sent.current = true;
        track('investor_section_view', {
          surface: 'page',
          listing_key: listingKey,
          listing_type: 'sale',
          listing_source: 'mls',
          rent_source: rentSource,
          has_remarks_rule: hasRemarksRule,
        });
        io.disconnect();
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [track, listingKey, rentSource, hasRemarksRule]);

  const onClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    const anchor = (event.target as HTMLElement).closest('a');
    if (!anchor) return;
    trackCtaClick('investor_rent_vs_mortgage', 'listing_as_a_rental', anchor.getAttribute('href') ?? undefined, {
      listing_key: listingKey,
      listing_type: 'sale',
      listing_source: 'mls',
    });
  };

  return (
    <div ref={ref} onClickCapture={onClickCapture}>
      {children}
    </div>
  );
}
